
import './App.css';
import { useState, useEffect, useRef, useCallback } from 'react';
import QRCode from 'react-qr-code';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { fetchPlaylist, selectPlaylist, PlayStatus, selectSelectedPlaylist, notifyPlayStart, notifyPlayEnd } from '../store/slices/playlistSlice';
import { useYouTubePlayer } from '../hooks/useYouTubePlayer';
import { YTPlayerState } from '../types/ytmusic';

/** YouTube URL에서 videoId를 추출합니다. 순수 ID가 들어오면 그대로 반환합니다. */
function extractVideoId(urlOrId: string): string {
  try {
    const url = new URL(urlOrId);
    return url.searchParams.get('v') ?? url.pathname.split('/').pop() ?? urlOrId;
  } catch {
    return urlOrId;
  }
}

/** 플레이어가 안정화될 때까지 대기하는 헬퍼 */
const waitForStable = (ms = 150) => new Promise<void>(resolve => setTimeout(resolve, ms));

interface RequestedTrack {
  id: string;
  videoId: string;
  title: string;
  timestamp: number;
  playOrigin: string;
  rqstPriority: string;
}

/** 통합 플레이리스트 아이템 — YouTube 음악과 신청곡을 하나의 리스트로 관리 */
interface UnifiedTrack {
  videoId: string;
  title: string;
  artistNm: string;
  duration: string;
  playOrigin: 'MUSIC' | 'VIDEO';
  /** 신청곡은 서버 playlistId, YouTube 뮤직은 빈 문자열 */
  playlistId: string;
  rqstPriority: string;
  thumbnailUrl: string;
  contentUrl: string;
  /** 서버 contentId — notifyPlayStart 전송에 필요 */
  contentId: string;
}
export const QR_URL_PREFIX = 'https://summitplay-jukebox-client.web.app/store/'

export default function App() {
  const playlist = useAppSelector(selectPlaylist);
  const selectedPlaylist = useAppSelector(selectSelectedPlaylist);
  const storeUid = useAppSelector((state) => state.store.storeInfo?.storeUid ?? '');
  const dispatch = useAppDispatch();

  const qrRef = useRef<HTMLDivElement>(null);
  const qrValue = QR_URL_PREFIX + storeUid;

  const handleQrDownload = useCallback(() => {
    const svgEl = qrRef.current?.querySelector('svg');
    if (!svgEl) return;

    const svgData = new XMLSerializer().serializeToString(svgEl);
    const canvas = document.createElement('canvas');
    const size = 256;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = 'qrcode.png';
      a.click();
    };
    img.src = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgData)))}`;
  }, []);

  // --- 사이드바 UI 표시용 신청곡 목록 ---
  const [requestedTracks, setRequestedTracks] = useState<RequestedTrack[]>([]);
  // 현재 재생 중인 신청곡 ID (null이면 MUSIC 트랙)
  const [currentPlayingRequestId, setCurrentPlayingRequestId] = useState<string | null>(null);
  // 현재 트랙의 playOrigin
  const [playOrigin, setPlayOrigin] = useState<string>('MUSIC');

  // --- 통합 플레이리스트 — 재생 순서의 단일 소스 ---
  const [unifiedPlaylist, setUnifiedPlaylist] = useState<UnifiedTrack[]>([]);
  // 통합 플레이리스트에서 현재 재생 중인 인덱스
  const [currentUnifiedIndex, setCurrentUnifiedIndex] = useState<number>(0);
  // YouTube 플레이리스트 초기 로드 완료 여부
  const ytPlaylistInitializedRef = useRef(false);

  // dispatch를 ref로 유지 — useCallback 클로저에서 사용
  const dispatchRef = useRef(dispatch);
  dispatchRef.current = dispatch;

  // 트랙 전환 가드 — 전환 중 중복 loadVideo 호출 방지
  const isTransitioningRef = useRef(false);
  // 전환 가드 타임아웃 — 백그라운드 탭에서 PLAYING 상태 미도달 시 자동 해제
  const transitionTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 마지막 iframe 재생성 시각 — 주기적 재생성으로 GPU 메모리 누수 방지
  const lastRecreationTimeRef = useRef<number>(Date.now());
  // 초기 플레이리스트 로드 완료 여부 — recreatePlayer 시 useEffect 재실행 방지
  const initialLoadedPlaylistRef = useRef<string | null>(null);
  // 전환 타임아웃에서 사용할 play 함수 ref (훅 선언 순서 문제로 ref 사용)
  const forcePlayRef = useRef<(() => void) | null>(null);
  // 전환 타임아웃에서 player 상태 확인용 ref
  const getVideoUrlRef = useRef<(() => string) | null>(null);
  // 현재 로드 요청한 videoId — bg polling에서 실제 로드 여부 확인용
  const expectedVideoIdRef = useRef<string>('');

  // 신청곡 재생 추적 — playTms: 재생 중 초 단위 시간 기록, playStTm: 시작 시각, playMaxTm: 종료 시간(정수)
  const playTmsSetRef = useRef<Set<number>>(new Set());
  const playStTmRef = useRef<number>(0);
  const playMaxTmRef = useRef<number>(0);

  // 최신 상태를 ref로 유지하여 콜백에서 참조
  const stateRef = useRef({
    requestedTracks,
    currentPlayingRequestId,
    playOrigin,
    playlist,
    unifiedPlaylist,
    currentUnifiedIndex,
  });

  // 상태가 변경될 때마다 ref 업데이트
  useEffect(() => {
    stateRef.current = {
      requestedTracks,
      currentPlayingRequestId,
      playOrigin,
      playlist,
      unifiedPlaylist,
      currentUnifiedIndex,
    };
  }, [requestedTracks, currentPlayingRequestId, playOrigin, playlist, unifiedPlaylist, currentUnifiedIndex]);

  // 전환 가드 설정 — 5초 후 자동 해제 (백그라운드 탭 stuck 방지)
  // 단, 새 비디오가 아직 로드되지 않았으면 가드를 연장
  const transitionRetryCountRef = useRef(0);
  const startTransition = useCallback(() => {
    isTransitioningRef.current = true;
    transitionRetryCountRef.current = 0;
    if (transitionTimeoutRef.current) clearTimeout(transitionTimeoutRef.current);

    const scheduleTimeout = () => {
      transitionTimeoutRef.current = setTimeout(() => {
        if (!isTransitioningRef.current) {
          transitionTimeoutRef.current = null;
          return;
        }

        // 새 비디오가 로드되었는지 확인
        const playerUrl = getVideoUrlRef.current?.() ?? '';
        const expectedId = expectedVideoIdRef.current;
        const videoLoaded = !expectedId || !playerUrl || playerUrl.includes(expectedId);

        if (!videoLoaded && transitionRetryCountRef.current < 3) {
          // 아직 이전 비디오 상태 → 가드를 유지하고 3초 후 재시도 (최대 3회)
          transitionRetryCountRef.current++;
          console.warn(`[Transition] 타임아웃 — 새 비디오 미로드, 가드 연장 (${transitionRetryCountRef.current}/3)`);
          scheduleTimeout();
          return;
        }

        console.warn(`[Transition] 타임아웃 — 전환 가드 자동 해제, playVideo() 강제 호출${!videoLoaded ? ' (비디오 미로드 상태)' : ''}`);
        isTransitioningRef.current = false;
        forcePlayRef.current?.();
        transitionTimeoutRef.current = null;
      }, transitionRetryCountRef.current === 0 ? 5000 : 3000);
    };

    scheduleTimeout();
  }, []);

  const clearTransition = useCallback(() => {
    isTransitioningRef.current = false;
    if (transitionTimeoutRef.current) {
      clearTimeout(transitionTimeoutRef.current);
      transitionTimeoutRef.current = null;
    }
  }, []);

  // YouTube Player 초기화
  const {
    containerRef,
    player,
    isReady,
    loadVideo,
    loadPlaylist,
    getCurrentTime,
    getDuration,
    getVideoUrl,
    recreatePlayer,
    play: forcePlay,
  } = useYouTubePlayer({
    playerVars: {
      autoplay:    0,
      controls:    1,
      autohide:    0,
      disablekb:   1,
      rel:         0,
      playsinline: 1,
      fs:          0,
      iv_load_policy: 3,
      modestbranding: 1,
    },
    onReady:       () => {},
    onStateChange: (s, _player) => {
      // recreatePlayer 후 autoplay 차단 시 CUED/PAUSED/UNSTARTED 상태에서 강제 재생
      if (isTransitioningRef.current && (s === YTPlayerState.CUED || s === YTPlayerState.PAUSED || s === YTPlayerState.UNSTARTED)) {
        console.log(`[Transition] 전환 중 state=${s} 감지 — playVideo() 강제 호출`);
        _player.playVideo();
        return;
      }
      handleStateChange(s);
    },
    onEnded:       () => {},
    onError:       (code, msg, videoId) => {
      console.error(`[YT Error] code=${code}, msg=${msg}, videoId=${videoId}`);
      clearTransition();
    },
  });

  // forcePlay ref 연결 — startTransition 타임아웃에서 사용
  forcePlayRef.current = forcePlay;
  // getVideoUrl ref 연결 — startTransition 타임아웃에서 비디오 로드 여부 확인용
  getVideoUrlRef.current = getVideoUrl;

  // 신청곡 재생 중 매 초마다 현재 재생 시간을 Set에 기록
  useEffect(() => {
    if (!player || !currentPlayingRequestId) return;
    const interval = setInterval(() => {
      const ct = getCurrentTime();
      if (ct > 0) {
        playTmsSetRef.current.add(Math.floor(ct));
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [player, currentPlayingRequestId, getCurrentTime]);

  // 안전하게 단일 영상으로 전환하는 헬퍼 (recreatePlayer + loadVideo)
  const safeLoadVideo = useCallback(async (videoId: string) => {
    try {
      await recreatePlayer();
      await waitForStable();
      loadVideo(videoId);
    } catch (err) {
      console.error('[safeLoadVideo] error:', err);
      clearTransition();
    }
  }, [recreatePlayer, loadVideo]);

  // --- 통합 플레이리스트에서 다음 곡을 재생하는 핵심 함수 ---
  const playNextFromUnified = useCallback((currentIdx: number, currentTrack: UnifiedTrack | null, unified: UnifiedTrack[]) => {
    let nextIndex: number;

    if (currentTrack && currentTrack.playOrigin === 'VIDEO') {
      // 신청곡이 끝남 → 통합 플레이리스트에서 제거 → 같은 인덱스가 다음 곡
      const filtered = unified.filter(t => t.playlistId !== currentTrack.playlistId);
      setUnifiedPlaylist(prev => prev.filter(t => t.playlistId !== currentTrack.playlistId));
      setRequestedTracks(prev => prev.filter(t => t.id !== currentTrack.playlistId));

      // 제거 후 같은 인덱스 위치가 다음 곡 (인덱스가 범위를 넘으면 순환)
      nextIndex = currentIdx >= filtered.length ? 0 : currentIdx;

      if (filtered.length === 0) {
        console.log('[playNext] 통합 플레이리스트가 비어있음 — 재생 중지');
        setCurrentPlayingRequestId(null);
        setPlayOrigin('MUSIC');
        return;
      }

      const nextTrack = filtered[nextIndex];
      console.log(`[playNext] 신청곡 종료 → 다음 곡: "${nextTrack.title || nextTrack.videoId}" (${nextTrack.playOrigin}), index=${nextIndex}`);

      expectedVideoIdRef.current = nextTrack.videoId;
      startTransition();
      setCurrentUnifiedIndex(nextIndex);

      // 13분 이상 경과 시 곡 전환 타이밍에 recreatePlayer (15분 근처에서 미리 실행하여 재생 중 끊김 방지)
      // 단, 백그라운드 탭에서는 recreatePlayer 금지 (새 iframe은 autoplay 권한이 없어 재생 불가)
      const timeSinceRecreation = Date.now() - lastRecreationTimeRef.current;
      const needsRecreate = timeSinceRecreation > 13 * 60 * 1000;
      const isBackground = document.hidden;

      const doLoad = async () => {
        if (needsRecreate && !isBackground) {
          lastRecreationTimeRef.current = Date.now();
          await safeLoadVideo(nextTrack.videoId);
        } else {
          loadVideo(nextTrack.videoId);
        }

        if (nextTrack.playOrigin === 'VIDEO') {
          setCurrentPlayingRequestId(nextTrack.playlistId);
          setPlayOrigin('VIDEO');
          // UnifiedTrack에 저장된 데이터로 PlaylistItem 구성 — playlist.find() 시점 차이로 인한 누락 방지
          const playlistItem = stateRef.current.playlist.find(p => p.playlistId === nextTrack.playlistId);
          const itemForNotify = playlistItem ?? {
            playlistId:     nextTrack.playlistId,
            contentId:      nextTrack.contentId,
            title:          nextTrack.title,
            artistNm:       nextTrack.artistNm,
            duration:       nextTrack.duration,
            thumbnailUrl:   nextTrack.thumbnailUrl,
            contentUrl:     nextTrack.contentUrl,
            rqstPriority:   nextTrack.rqstPriority,
            fbUid:          '',
            type:           '',
            playStts:       'QUEUE' as const,
            musicVideoId:   '',
            musicVideoFbUid:'',
            reqDt:          '',
          };
          console.log(`[PlayStart] 신청곡 시작 전송: "${itemForNotify.title}", id=${nextTrack.playlistId}${!playlistItem ? ' (playlist에서 미발견 — UnifiedTrack 폴백)' : ''}`);
          dispatchRef.current(notifyPlayStart({ item: itemForNotify }));
          playTmsSetRef.current = new Set();
          playStTmRef.current = Date.now();
          playMaxTmRef.current = 0;
        } else {
          setCurrentPlayingRequestId(null);
          setPlayOrigin('MUSIC');
        }
      };

      doLoad();
    } else {
      // MUSIC 트랙이 끝남 → 다음 인덱스로 이동 (순환)
      nextIndex = (currentIdx + 1) % unified.length;
      const nextTrack = unified[nextIndex];
      console.log(`[playNext] MUSIC 종료 → 다음 곡: "${nextTrack.title || nextTrack.videoId}" (${nextTrack.playOrigin}), index=${nextIndex}`);

      expectedVideoIdRef.current = nextTrack.videoId;
      startTransition();
      setCurrentUnifiedIndex(nextIndex);

      // 13분 이상 경과 시 곡 전환 타이밍에 recreatePlayer (15분 근처에서 미리 실행하여 재생 중 끊김 방지)
      // 단, 백그라운드 탭에서는 recreatePlayer 금지
      const timeSinceRecreation = Date.now() - lastRecreationTimeRef.current;
      const needsRecreate = timeSinceRecreation > 13 * 60 * 1000;
      const isBackground = document.hidden;

      const doLoad = async () => {
        if (needsRecreate && !isBackground) {
          lastRecreationTimeRef.current = Date.now();
          await safeLoadVideo(nextTrack.videoId);
        } else {
          loadVideo(nextTrack.videoId);
        }

        if (nextTrack.playOrigin === 'VIDEO') {
          setCurrentPlayingRequestId(nextTrack.playlistId);
          setPlayOrigin('VIDEO');
          // UnifiedTrack에 저장된 데이터로 PlaylistItem 구성 — playlist.find() 시점 차이로 인한 누락 방지
          const playlistItem = stateRef.current.playlist.find(p => p.playlistId === nextTrack.playlistId);
          const itemForNotify = playlistItem ?? {
            playlistId:     nextTrack.playlistId,
            contentId:      nextTrack.contentId,
            title:          nextTrack.title,
            artistNm:       nextTrack.artistNm,
            duration:       nextTrack.duration,
            thumbnailUrl:   nextTrack.thumbnailUrl,
            contentUrl:     nextTrack.contentUrl,
            rqstPriority:   nextTrack.rqstPriority,
            fbUid:          '',
            type:           '',
            playStts:       'QUEUE' as const,
            musicVideoId:   '',
            musicVideoFbUid:'',
            reqDt:          '',
          };
          console.log(`[PlayStart] 신청곡 시작 전송: "${itemForNotify.title}", id=${nextTrack.playlistId}${!playlistItem ? ' (playlist에서 미발견 — UnifiedTrack 폴백)' : ''}`);
          dispatchRef.current(notifyPlayStart({ item: itemForNotify }));
          playTmsSetRef.current = new Set();
          playStTmRef.current = Date.now();
          playMaxTmRef.current = 0;
        } else {
          setCurrentPlayingRequestId(null);
          setPlayOrigin('MUSIC');
        }
      };

      doLoad();
    }
  }, [safeLoadVideo, loadVideo, startTransition]);

  // --- 신청곡 종료 서버 전송 헬퍼 ---
  const sendPlayEnd = useCallback((trackPlaylistId: string) => {
    const endedItem = stateRef.current.playlist.find(p => p.playlistId === trackPlaylistId);
    if (endedItem) {
      const playTms = Array.from(playTmsSetRef.current);
      const playStTm = playStTmRef.current;
      const playMaxTm = Math.floor(getDuration());
      console.log(`[PlayEnd] 신청곡 종료 전송: "${endedItem.title}", playTms=${playTms.length}초, playMaxTm=${playMaxTm}s`);
      dispatchRef.current(notifyPlayEnd({ item: { ...endedItem, playTms, playStTm, playMaxTm, playOrigin: 'VIDEO', reqContentAt: 'N' } }));
    }
    playTmsSetRef.current = new Set();
    playStTmRef.current = 0;
    playMaxTmRef.current = 0;
  }, [getDuration]);

  // 상태 변경 핸들러 — 통합 플레이리스트 기반
  const handleStateChange = useCallback((s: YTPlayerState) => {
    const state = stateRef.current;

    // 전환 중이면 PLAYING이 올 때까지 무시
    if (isTransitioningRef.current) {
      if (s === YTPlayerState.PLAYING) {
        clearTransition();
      }
      return;
    }

    // ENDED 상태일 때 — 통합 플레이리스트에서 다음 곡 재생
    if (s === YTPlayerState.ENDED) {
      const currentTrack = state.unifiedPlaylist[state.currentUnifiedIndex] ?? null;
      console.log(`[StateChange] ENDED: index=${state.currentUnifiedIndex}, track="${currentTrack?.title || currentTrack?.videoId || 'none'}" (${currentTrack?.playOrigin}), 통합=${state.unifiedPlaylist.length}곡`);

      // 현재 트랙이 신청곡이면 종료 전송
      if (currentTrack && currentTrack.playOrigin === 'VIDEO') {
        sendPlayEnd(currentTrack.playlistId);
      }

      playNextFromUnified(state.currentUnifiedIndex, currentTrack, state.unifiedPlaylist);
    }
  }, [playNextFromUnified, sendPlayEnd, clearTransition]);

  // --- 백그라운드 탭 폴링: YouTube 이벤트가 발생하지 않을 때 곡 종료를 직접 감지 ---
  const bgPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const startBgPolling = () => {
      if (bgPollingRef.current) return; // 이미 폴링 중
      console.log('[BgPolling] 백그라운드 진입 — 폴링 시작 (2초 간격)');

      bgPollingRef.current = setInterval(() => {
        // 전환 중이면 건너뜀
        if (isTransitioningRef.current) return;

        const duration = getDuration();
        const currentTime = getCurrentTime();
        // duration이 0이면 아직 영상 미로드 → 건너뜀
        if (duration <= 0) return;

        // 현재 player에 로드된 비디오가 예상 곡과 다르면 건너뜀 (stale state 방지)
        const playerUrl = getVideoUrl();
        const expectedId = expectedVideoIdRef.current;
        if (expectedId && playerUrl && !playerUrl.includes(expectedId)) {
          return;
        }

        // 현재 재생 위치가 전체 길이에 근접하면 곡 종료로 판단
        if (currentTime >= duration - 1.5) {
          const state = stateRef.current;
          const currentTrack = state.unifiedPlaylist[state.currentUnifiedIndex] ?? null;
          console.log(`[BgPolling] 곡 종료 감지: currentTime=${currentTime.toFixed(1)}, duration=${duration.toFixed(1)}, track="${currentTrack?.title || currentTrack?.videoId || 'none'}"`);

          // 신청곡이면 종료 전송
          if (currentTrack && currentTrack.playOrigin === 'VIDEO') {
            sendPlayEnd(currentTrack.playlistId);
          }

          playNextFromUnified(state.currentUnifiedIndex, currentTrack, state.unifiedPlaylist);
        }
      }, 2000);
    };

    const stopBgPolling = () => {
      if (bgPollingRef.current) {
        console.log('[BgPolling] 포그라운드 복귀 — 폴링 중지');
        clearInterval(bgPollingRef.current);
        bgPollingRef.current = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        startBgPolling();
      } else {
        stopBgPolling();
        // 포그라운드 복귀 시 15분 타이머를 리셋 — 포그라운드 사용 시간 기준으로만 recreate
        lastRecreationTimeRef.current = Date.now();
        console.log('[Visibility] 포그라운드 복귀 — recreate 타이머 리셋');
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    // 이미 백그라운드 상태에서 마운트된 경우 대비
    if (document.hidden) startBgPolling();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      stopBgPolling();
    };
  }, [getCurrentTime, getDuration, getVideoUrl, playNextFromUnified, sendPlayEnd]);

  // 신청곡 목록 주기적으로 갱신
  useEffect(() => {
    dispatch(fetchPlaylist());
    const interval = setInterval(() => dispatch(fetchPlaylist()), 10_000);
    return () => clearInterval(interval);
  }, [dispatch]);

  // playlist에서 대기(QUEUE) 또는 재생중(PLAY) 상태인 신청곡을 requestedTracks에 동기화 (UI용)
  // — ENDED 상태가 되면 서버 동기화 시 자연스럽게 목록에서 제거됨
  useEffect(() => {
    const active = playlist.filter(item =>
      item.playStts === PlayStatus.WAITING || item.playStts === PlayStatus.PLAYING
    );
    setRequestedTracks(active.map(item => ({
      id:            item.playlistId,
      videoId:       extractVideoId(item.contentUrl),
      title:         item.title,
      timestamp:     new Date(item.reqDt).getTime(),
      playOrigin:    'VIDEO',
      rqstPriority:  item.rqstPriority,
    })));
  }, [playlist]);

  // YouTube 플레이리스트 선택 시 → YouTube API로 영상 목록 가져와서 통합 플레이리스트 초기화
  useEffect(() => {
    if (!isReady || !selectedPlaylist) return;

    // 같은 플레이리스트가 이미 로드되었으면 건너뜀
    if (initialLoadedPlaylistRef.current === selectedPlaylist.youtubePlaylistId) return;
    initialLoadedPlaylistRef.current = selectedPlaylist.youtubePlaylistId;

    // YouTube 플레이리스트를 로드하여 영상 ID 목록을 가져옴
    ytPlaylistInitializedRef.current = false;
    setUnifiedPlaylist([]);

    // YouTube Player의 loadPlaylist로 영상 목록을 가져온 뒤 개별 영상 재생으로 전환
    loadPlaylist(selectedPlaylist.youtubePlaylistId, 0);
    setPlayOrigin('MUSIC');
    setCurrentPlayingRequestId(null);
    setCurrentUnifiedIndex(0);
  }, [isReady, selectedPlaylist?.youtubePlaylistId, loadPlaylist]);

  // YouTube 플레이리스트가 로드된 후 영상 목록을 통합 플레이리스트로 옮기고 개별 재생 모드로 전환
  useEffect(() => {
    if (!player || ytPlaylistInitializedRef.current) return;

    const timer = setTimeout(() => {
      try {
        const ytVideoIds = player.getPlaylist();
        if (!ytVideoIds || ytVideoIds.length === 0) return;

        const ytTracks: UnifiedTrack[] = ytVideoIds.map(vid => ({
          videoId:       vid,
          title:         '',
          artistNm:      '',
          duration:      '',
          playOrigin:    'MUSIC' as const,
          playlistId:    '',
          rqstPriority:  '',
          thumbnailUrl:  '',
          contentUrl:    '',
          contentId:     '',
        }));

        setUnifiedPlaylist(ytTracks);
        ytPlaylistInitializedRef.current = true;
        console.log(`[UnifiedPlaylist] YouTube 플레이리스트 초기화: ${ytVideoIds.length}곡`);

        // YouTube native playlist 모드를 종료하고 개별 영상 재생 모드로 전환
        // — loadPlaylist 이후 YouTube가 자체적으로 다음 곡을 넘기는 동작을 차단하고,
        //   이후 모든 재생 제어를 unifiedPlaylist의 playNextFromUnified가 담당
        const currentIndex = player.getPlaylistIndex?.() ?? 0;
        setCurrentUnifiedIndex(currentIndex);
        const startVideoId = ytVideoIds[currentIndex] ?? ytVideoIds[0];
        console.log(`[UnifiedPlaylist] native playlist 모드 종료 → loadVideo("${startVideoId}"), index=${currentIndex}`);
        startTransition();
        loadVideo(startVideoId);
      } catch {
        // player가 아직 준비되지 않은 경우
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [player, loadVideo]);

  // 신청곡 동기화: 서버 playlist 순서 기준으로 통합 플레이리스트의 VIDEO 트랙을 재배치
  useEffect(() => {
    setUnifiedPlaylist(prev => {
      if (prev.length === 0) return prev;

      const result = [...prev];
      const currentIndex = stateRef.current.currentUnifiedIndex;
      const currentPlayingId = stateRef.current.currentPlayingRequestId;
      const activePlaylistIds = new Set(playlist.map(item => item.playlistId));

      // 1) 현재 재생중인 곡을 제외한 모든 VIDEO 트랙을 제거
      let removedBeforeCurrent = 0;
      for (let i = result.length - 1; i >= 0; i--) {
        const t = result[i];
        if (t.playOrigin !== 'VIDEO' || t.playlistId === currentPlayingId) continue;
        if (!activePlaylistIds.has(t.playlistId)) {
          console.log(`[UnifiedPlaylist] 서버에서 삭제된 신청곡 제거: "${t.title}"`);
        }
        if (i < currentIndex) removedBeforeCurrent++;
        result.splice(i, 1);
      }

      const effectiveCurrentIndex = currentIndex - removedBeforeCurrent;
      if (removedBeforeCurrent > 0) {
        setCurrentUnifiedIndex(prevIdx => prevIdx - removedBeforeCurrent);
      }

      // 2) 서버 playlist의 WAITING 곡을 서버 순서대로 재삽입
      const queued = playlist.filter(item => item.playStts === PlayStatus.WAITING);
      let insertAt = effectiveCurrentIndex + 1;
      // 현재 재생중인 VIDEO 트랙(currentPlayingId)이 있으면 그 뒤로 이동
      if (insertAt < result.length && result[insertAt]?.playOrigin === 'VIDEO' && result[insertAt]?.playlistId === currentPlayingId) {
        insertAt++;
      }

      for (const item of queued) {
        // 현재 재생중인 곡은 이미 통합 플레이리스트에 유지되므로 skip
        if (item.playlistId === currentPlayingId) continue;

        const track: UnifiedTrack = {
          videoId:       extractVideoId(item.contentUrl),
          title:         item.title,
          artistNm:      item.artistNm,
          duration:      item.duration,
          playOrigin:    'VIDEO',
          playlistId:    item.playlistId,
          rqstPriority:  item.rqstPriority,
          thumbnailUrl:  item.thumbnailUrl,
          contentUrl:    item.contentUrl,
          contentId:     item.contentId,
        };

        result.splice(insertAt, 0, track);
        console.log(`[UnifiedPlaylist] 신청곡 삽입: "${item.title}" at index ${insertAt}`);
        insertAt++;
      }

      return result;
    });
  }, [playlist]);

  return (
      <div className="app">
        <main className="player-area">
          <div className="player-header">
          <span className="player-url">
            ▶ Play Some Tunes
            <span className="api-tag"></span>
          </span>
            <span className="player-hint"></span>
          </div>

          <div className="player-container">
            <div ref={containerRef} className="yt-player-mount" />
          </div>
        </main>

        <aside className="request-sidebar">
          <div className="request-header">
            <h2>🎤 신청곡 플레이리스트</h2>
            <span className="request-count">
              {requestedTracks.length}곡
            </span>
          </div>

          <div className="request-list">
            {requestedTracks.length === 0 ? (
                <div className="request-empty">신청곡이 없습니다</div>
            ) : (
                requestedTracks.map((item) => {
                  const isCurrentlyPlaying = item.id === currentPlayingRequestId;

                  return (
                      <div
                          key={item.id}
                          className={`request-item ${isCurrentlyPlaying ? 'playing' : ''}`}
                      >
                        <div className="request-info">
                          <span className="request-title">{item.title}</span>
                          <div className="request-meta">
                            <span className={`request-status ${isCurrentlyPlaying ? 'status-play' : 'status-queue'}`}>
                              {isCurrentlyPlaying ? '▶ 재생중' : '⏳ 대기'}
                            </span>
                          </div>
                        </div>
                      </div>
                  );
                })
            )}
          </div>

          <div className="qr-section">
            <div className="qr-label">신청곡 QR 코드</div>
            <div ref={qrRef} className="qr-code-wrapper">
              <QRCode value={qrValue} size={160} />
            </div>
            <button className="qr-download-btn" onClick={handleQrDownload} disabled={!storeUid}>
              QR 코드 다운로드
            </button>
            <button className="qr-download-btn" onClick={() => window.open('https://summitplay.notion.site/Play-Some-Tunes-34f77dadb82080a5a4edc29f483d71f4?source=copy_link', '_blank')}>
              이용 가이드
            </button>
            <button className="qr-download-btn" onClick={() => window.open(`https://summitplay.hakine.com/api/office/pst/monitor/index?uid=${storeUid}`, '_blank', 'width=1280,height=800,resizable=yes,scrollbars=yes')} disabled={!storeUid}>
              신청곡 관리
            </button>
          </div>
        </aside>
      </div>
  );
}
